import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppStore } from '../store/useAppStore';
import { useSettingsStore } from '../store/useSettingsStore';
import { isCustomerOrder, shouldAlertOrder } from '../utils/orderUtils';
import { getRemainingSeconds, getDefaultDuration } from '../utils/bakingTime';
import { vibrate, stopVibration, overdueVibrationPattern } from '../utils/vibration';
import { isAppHidden, isStandalone, notify } from '../utils/pwa';
import { playBell } from '../utils/audio';
import { useRole } from './useRole';

export function useOrderAlerts() {
  const { t } = useTranslation();
  const orders = useAppStore((state) => state.orders);
  const now = useAppStore((state) => state.now);
  const mode = useSettingsStore((state) => state.mode);
  const currentCustomer = useSettingsStore((state) => state.currentCustomer);
  const config = useAppStore((state) => state.config);
  const pinConfigured = useAppStore((state) => state.pinConfigured);
  const setStatusMessage = useAppStore((state) => state.setStatusMessage);
  const role = useRole();

  const alertStates = useRef(new Map());
  const missedVibrations = useRef(new Set());
  const bakerVibrating = useRef(new Set());
  const configDurationSeconds = getDefaultDuration(config);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const hidden = isAppHidden();
    const installed = isStandalone();

    for (const orderId of bakerVibrating.current) {
      if (!orders.some((order) => order.id === orderId)) {
        bakerVibrating.current.delete(orderId);
      }
    }

    // The baker-duty path (all-order alerts: 1 strike, 5x3 vibration, own
    // done-transitions silenced) applies to baker-role users with a configured
    // BAKER_PIN on every page, and to users in an open config while the baker
    // page is active. Everyone else only gets customer-style alerts (2
    // strikes, 3x3 vibration + notification) for their own orders.
    const bakerDuty = role === 'baker' && (pinConfigured === true || mode === 'baker');

    orders.forEach((order) => {
      const isCustomer = isCustomerOrder(order, currentCustomer);
      const remaining = getRemainingSeconds(order, now, configDurationSeconds);

      // Ready and overtime are the same for the customer: alert once per
      // condition period, whichever comes first — the order reaching `done`
      // or the baking timer running out while still `baking`.
      const ready =
        order.state === 'done' ||
        (order.state === 'baking' && remaining !== null && remaining <= 0);

      if (!ready) {
        alertStates.current.delete(order.id);
        missedVibrations.current.delete(order.id);
        bakerVibrating.current.delete(order.id);
        return;
      }

      // Baker-duty alerts are pointless for a `baking -> done` transition the
      // baker performed themselves: the duty bell/vibration only fires when a
      // baking order's timer runs out (order still `baking`), and an ongoing
      // duty vibration is cut short once that order reaches `done`.
      if (bakerDuty && order.state === 'done' && bakerVibrating.current.delete(order.id)) {
        stopVibration();
      }

      const bakerAction = bakerDuty && order.state === 'done';

      const state = alertStates.current.get(order.id) || { sound: false, vibrated: false, notified: false };
      const relevant = shouldAlertOrder(order, mode, currentCustomer, role, pinConfigured);

      if (relevant && !state.sound && !bakerAction) {
        state.sound = true;
        playBell(bakerDuty ? 1 : 2);
        if (isCustomer) {
          setStatusMessage(t('alert.ready', { name: order.customerName }));
        }
      }

      if (relevant && !state.vibrated && !bakerAction) {
        state.vibrated = true;
        if (hidden) {
          missedVibrations.current.add(order.id);
        } else {
          vibrate(overdueVibrationPattern(bakerDuty ? 5 : 3));
          if (bakerDuty) {
            bakerVibrating.current.add(order.id);
          }
        }
      }

      if (isCustomer && !bakerDuty && !state.notified && hidden && installed) {
        state.notified = true;
        notify(t('notif.readyTitle'), {
          body: t('notif.readyBody', { name: order.customerName, orderNo: order.orderNo }),
          tag: order.id,
        });
      }

      alertStates.current.set(order.id, state);
    });
  }, [orders, now, mode, currentCustomer, role, pinConfigured, configDurationSeconds, setStatusMessage, t]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;

      const bakerDuty = role === 'baker' && (pinConfigured === true || mode === 'baker');

      const ids = Array.from(missedVibrations.current);
      for (const orderId of ids) {
        const order = orders.find((entry) => entry.id === orderId);
        if (!order) {
          missedVibrations.current.delete(orderId);
          continue;
        }
        const remaining = getRemainingSeconds(order, Date.now(), configDurationSeconds);
        const stillRelevant =
          order.state === 'done' ||
          (order.state === 'baking' && remaining !== null && remaining <= 0);
        if (!stillRelevant) {
          missedVibrations.current.delete(orderId);
          continue;
        }
        // In baker-duty mode only the timer-ran-out case is replayed; done
        // transitions are the baker's own action.
        if (bakerDuty && order.state === 'done') {
          missedVibrations.current.delete(orderId);
          continue;
        }
        const isCustomer = isCustomerOrder(order, currentCustomer);
        if (bakerDuty || isCustomer) {
          vibrate(overdueVibrationPattern(bakerDuty ? 5 : 3));
          if (bakerDuty) {
            bakerVibrating.current.add(orderId);
          }
        }
        missedVibrations.current.delete(orderId);
      }
    };

    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [orders, mode, currentCustomer, role, pinConfigured, configDurationSeconds]);
}
